/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useMemo, useState } from "react";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import { autoScrollJoyrideStep } from "@/libs/joyrideAutoScroll";

type FundingTrackingTutorialMode = "project" | "funder";

const TUTORIAL_DONE_KEY = "verdeo_funding_tracking_joyride_v2_done";
const FILTER_PROJECT_STEP_TARGET = '[data-tour-funding-project-filter-panel="true"]';
const PROJECT_CARD_STEP_TARGET =
  '[data-tour-funding-project-card="true"] [data-tour-funding-project-card-header="true"]';
const STICKY_TIMELINE_HEADER_SELECTOR = '[data-tour-funding-page-timeline-header="true"]';

function stepExists(step: Step): boolean {
  if (!step.target || step.target === "body") return true;
  if (typeof step.target !== "string") return true;
  try {
    return !!document.querySelector(step.target);
  } catch {
    return false;
  }
}

export default function FundingTrackingTutorial({
  mode,
  showProjectFilter = false,
  showFunderSearch = false,
}: {
  mode: FundingTrackingTutorialMode;
  showProjectFilter?: boolean;
  showFunderSearch?: boolean;
}) {
  const [run, setRun] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [resolvedSteps, setResolvedSteps] = useState<Step[]>([]);
  const [activeStepTarget, setActiveStepTarget] = useState<string | null>(null);

  const steps = useMemo<Step[]>(() => {
    if (mode === "project") {
      return [
        {
          target: "body",
          placement: "center",
          disableBeacon: true,
          content: (
            <div className="text-center">
              <h3 className="mb-2 text-base font-semibold">
                Bienvenue dans le suivi de financement
              </h3>
              <p className="text-sm">
                Ce guide vous montre où consulter les contrats, les versements
                reçus et les versements terrain.
              </p>
            </div>
          ),
        },
        ...(showProjectFilter
          ? ([
              {
                target: FILTER_PROJECT_STEP_TARGET,
                title: "Filtrer les projets",
                content:
                  "Utilisez ce bloc pour filtrer la liste des projets affichés avant de consulter les contrats.",
                placement: "bottom",
                disableBeacon: true,
              },
            ] as Step[])
          : []),
        {
          target: PROJECT_CARD_STEP_TARGET,
          title: "Projet et contrats",
          content:
            "Ouvrez un projet pour accéder à sa matrice mensuelle, à la liste des contrats signés et à leurs versements.",
          placement: "top",
          disableBeacon: true,
        },
        {
          target: '[data-tour-funding-project-matrix="true"]',
          title: "Vue mensuelle",
          content:
            "Cette zone résume les financements trouvés, les versements prévus, les versements reçus et les versements terrain sur l'année.",
          placement: "bottom",
          disableBeacon: true,
        },
        {
          target: '[data-tour-funding-project-detail="true"]',
          title: "Détail des versements",
          content:
            "Dans cette section, vous pouvez gérer les contrats et compléter les versements prévus, les versements reçus et les versements terrain.",
          placement: "top",
          disableBeacon: true,
        },
      ];
    }

    return [
      {
        target: "body",
        placement: "center",
        disableBeacon: true,
        content: (
          <div className="text-center">
            <h3 className="mb-2 text-base font-semibold">
              Bienvenue dans le suivi de financement
            </h3>
            <p className="text-sm">
              Cette vue vous permet de suivre les contrats et les versements par
              financeur puis par projet.
            </p>
          </div>
        ),
      },
      ...(showFunderSearch
        ? ([
            {
              target: '[data-tour-funding-funder-search="true"]',
              title: "Rechercher un financeur",
              content:
                "Utilisez la recherche pour retrouver rapidement un financeur dans la liste.",
              placement: "bottom",
              disableBeacon: true,
            },
          ] as Step[])
        : []),
      {
        target: '[data-tour-funding-funder-card="true"]',
        title: "Fiche financeur",
        content:
          "Dépliez un financeur pour afficher sa vue globale puis le détail des contrats et versements par projet.",
        placement: "top",
        disableBeacon: true,
      },
      {
        target: '[data-tour-funding-funder-summary="true"]',
        title: "Vue globale du financeur",
        content:
          "Cette zone regroupe les financements souhaités, les contrats signés et les versements reçus sur l'ensemble des projets du financeur.",
        placement: "top",
        disableBeacon: true,
      },
      {
        target: '[data-tour-funding-funder-project-detail="true"]',
        title: "Détail par projet",
        content:
          "Passez ensuite à cette section pour gérer les contrats, les versements reçus et les versements terrain projet par projet.",
        placement: "top",
        disableBeacon: true,
      },
    ];
  }, [mode, showFunderSearch, showProjectFilter]);

  useEffect(() => {
    setMounted(true);
    try {
      const hasSeen = window.localStorage.getItem(TUTORIAL_DONE_KEY);
      if (hasSeen === "1") return;
    } catch {
      // localStorage unavailable -> continue and show tutorial once
    }

    let attempts = 0;
    const maxAttempts = 30; // ~4.5s
    const timer = window.setInterval(() => {
      attempts += 1;
      const visibleSteps = steps.filter(stepExists);
      const minimumSteps =
        mode === "project" ? 3 : 3; // welcome + enough UI anchors
      if (visibleSteps.length >= minimumSteps || attempts >= maxAttempts) {
        setResolvedSteps(visibleSteps);
        setRun(true);
        window.clearInterval(timer);
      }
    }, 150);

    return () => window.clearInterval(timer);
  }, [mode, steps]);

  useEffect(() => {
    const shouldHideStickyHeader = mode === "project" && run;

    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>(STICKY_TIMELINE_HEADER_SELECTOR),
    );

    nodes.forEach((node) => {
      if (shouldHideStickyHeader) {
        if (!node.dataset.tourPrevVisibility) {
          node.dataset.tourPrevVisibility = node.style.visibility || "";
        }
        node.style.visibility = "hidden";
      } else if ("tourPrevVisibility" in node.dataset) {
        node.style.visibility = node.dataset.tourPrevVisibility || "";
        delete node.dataset.tourPrevVisibility;
      }
    });

    return () => {
      nodes.forEach((node) => {
        if ("tourPrevVisibility" in node.dataset) {
          node.style.visibility = node.dataset.tourPrevVisibility || "";
          delete node.dataset.tourPrevVisibility;
        }
      });
    };
  }, [mode, run]);

  useEffect(() => {
    if (!run || !activeStepTarget) return;
    if (typeof activeStepTarget !== "string") return;

    // Force un scroll précis sur certaines étapes car certains conteneurs sont très hauts
    // (carte projet complète) ou partiellement masqués par des éléments sticky.
    if (
      activeStepTarget !== PROJECT_CARD_STEP_TARGET &&
      activeStepTarget !== FILTER_PROJECT_STEP_TARGET
    ) {
      return;
    }

    const scrollToTarget = () => {
      const node = document.querySelector(activeStepTarget) as HTMLElement | null;
      if (!node) return;
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const raf = window.requestAnimationFrame(scrollToTarget);
    const timeout = window.setTimeout(scrollToTarget, 120);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
    };
  }, [activeStepTarget, run]);

  const handleCallback = (data: CallBackProps) => {
    autoScrollJoyrideStep(data);
    const { status, action, step } = data;
    if (typeof step?.target === "string") {
      setActiveStepTarget(step.target);
    }
    const doneStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
    if (doneStatuses.includes(status) || action === "skip") {
      setRun(false);
      setActiveStepTarget(null);
      try {
        window.localStorage.setItem(TUTORIAL_DONE_KEY, "1");
      } catch {
        // noop
      }
    }
  };

  if (!mounted || resolvedSteps.length === 0) return null;

  return (
    <Joyride
      steps={resolvedSteps}
      run={run}
      callback={handleCallback}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      disableScrolling={false}
      spotlightClicks={false}
      locale={{
        back: "Précédent",
        close: "Fermer",
        last: "Terminer",
        next: "Suivant",
        skip: "Passer",
      }}
      styles={{
        options: {
          arrowColor: "#ffffff",
          backgroundColor: "#2f3a2f",
          overlayColor: "rgba(15, 23, 42, 0.2)",
          primaryColor: "#669523",
          textColor: "#ffffff",
          zIndex: 9999,
        },
        buttonBack: {
          color: "#ffffff",
          marginRight: 8,
        },
        buttonNext: {
          backgroundColor: "#669523",
        },
        buttonSkip: {
          color: "#ffffff",
        },
      }}
    />
  );
}
